import _extends from '@babel/runtime/helpers/esm/extends';
import { useMemo, useLayoutEffect } from 'react';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { textStyles, HeadingText, BodyText } from '@paypalcorp/pp-react-text';
import { devWarning, ZINDEX_MODAL, hasDocument } from '@paypalcorp/pp-react-utils';
import { Button } from '@paypalcorp/pp-react-buttons';

var DEFAULT_LINES_CLAMP_TITLE = 2;
var DEFAULT_LINES_CLAMP_BODY = 3;

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysSizeMediaSm = tokens.sysSizeMediaSm,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceJumbo = tokens.sysSpaceJumbo;

  var _textStyles = textStyles(tokens),
      lineClamping = _textStyles.lineClamping;

  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: error_view_base;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    height: ", ";\n  "])), sysSizeMediaSm);
  var wrapperStyles = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: wrapper;\n    display: flex;\n    flex-direction: column;\n  "])));
  var fullScreenBase = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: error_view_base_fullscreen;\n    position: fixed;\n    inset: 0 0 0 0;\n    background-color: ", ";\n    z-index: ", ";\n  "])), sysColorBackgroundSurface, ZINDEX_MODAL);
  var fullScreenWrapperStyles = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: wrapper_fullscreen;\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n    position: absolute;\n    top: 33%;\n    width: 100%;\n    padding: 0 ", ";\n  "])), sysSpaceJumbo);
  var titleStyles = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: title;\n    margin-bottom: ", ";\n    text-align: center;\n    ", "\n  "])), sysSpaceLg, lineClamping(DEFAULT_LINES_CLAMP_TITLE));
  var subTitleStyles = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: subtitle;\n    text-align: center;\n    margin-bottom: ", ";\n  "])), sysSpaceLg);
  var buttonWrapperStyles = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: button_wrap;\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n  "])));
  var primaryButtonStyles = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: primary_button;\n    margin: ", " 0 ", " 0;\n  "])), sysSpaceSm, sysSpaceSm);
  return {
    base: base,
    wrapperStyles: wrapperStyles,
    titleStyles: titleStyles,
    subTitleStyles: subTitleStyles,
    buttonWrapperStyles: buttonWrapperStyles,
    primaryButtonStyles: primaryButtonStyles,
    fullScreenBase: fullScreenBase,
    fullScreenWrapperStyles: fullScreenWrapperStyles
  };
}

var ErrorView = function (_ref) {
  var _ref$title = _ref.title,
      title = _ref$title === void 0 ? 'title' : _ref$title,
      _ref$subTitle = _ref.subTitle,
      subTitle = _ref$subTitle === void 0 ? 'sub title' : _ref$subTitle,
      primaryButtonOptions = _ref.primaryButtonOptions,
      tertiaryButtonOptions = _ref.tertiaryButtonOptions,
      _ref$fullScreen = _ref.fullScreen,
      fullScreen = _ref$fullScreen === void 0 ? false : _ref$fullScreen;

  /* istanbul ignore next */
  if (!primaryButtonOptions) {
    devWarning('Required prop `primaryButtonOptions` is missing');
  }

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  useLayoutEffect(function () {
    if (fullScreen && hasDocument()) {
      document.body.style.overflow = 'hidden';
    }

    return function () {
      if (fullScreen && hasDocument()) {
        document.body.style.overflow = 'auto';
      }
    };
  }, []);
  var baseStyles = _styles.base;
  var wrapperStyles = _styles.wrapperStyles;

  if (fullScreen) {
    baseStyles = _styles.fullScreenBase;
    wrapperStyles = _styles.fullScreenWrapperStyles;
  }

  return jsx("section", {
    css: baseStyles,
    "data-ppui-info": "error-view_1.5.12",
    "aria-live": "polite"
  }, jsx("div", {
    css: wrapperStyles,
    "data-ppui": "true"
  }, jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref2) {
    var css = _ref2.css;
    return jsx(HeadingText, {
      size: "sm",
      className: css(_styles.titleStyles),
      "data-ppui": "true"
    }, title);
  }), jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref3) {
    var css = _ref3.css;
    return jsx(BodyText, {
      clampAtLine: DEFAULT_LINES_CLAMP_BODY,
      className: css(_styles.subTitleStyles),
      "data-ppui": "true"
    }, subTitle);
  }), jsx("div", {
    css: _styles.buttonWrapperStyles,
    "data-ppui": "true"
  }, jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref4) {
    var _primaryButtonOptions;

    var cx = _ref4.cx,
        css = _ref4.css;
    return jsx(Button, {
      onClick: primaryButtonOptions === null || primaryButtonOptions === void 0 ? void 0 : primaryButtonOptions.onClick,
      className: cx(css(_styles.primaryButtonStyles), primaryButtonOptions === null || primaryButtonOptions === void 0 ? void 0 : primaryButtonOptions.className),
      "data-ppui": "true"
    }, (_primaryButtonOptions = primaryButtonOptions === null || primaryButtonOptions === void 0 ? void 0 : primaryButtonOptions.title) !== null && _primaryButtonOptions !== void 0 ? _primaryButtonOptions : 'title');
  }), tertiaryButtonOptions && jsx(Button, _extends({
    tertiary: true
  }, tertiaryButtonOptions, {
    "data-ppui": "true"
  }), tertiaryButtonOptions.title))));
};

export { ErrorView, styles as errorViewStyles };
